import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AppShell } from '@/shared/components/layout/AppShell';
import { ProtectedRoute } from '@/shared/auth/ProtectedRoute';
import { LoginPage } from '@/features/auth/LoginPage';
import { HomePage } from '@/features/home/HomePage';
import { VentasPage } from '@/features/ventas/VentasPage';
import { DetalleVentaPage } from '@/features/ventas/DetalleVentaPage';
import { DetalleVentaPagosPage } from '@/features/ventas/DetalleVentaPagosPage';
import { NuevaCotizacionPage } from '@/features/ventas/NuevaCotizacionPage';
import { ComprasPage } from '@/features/compras/ComprasPage';
import { NuevaCompraPage } from '@/features/compras/NuevaCompraPage';
import { VerCompraPage } from '@/features/compras/VerCompraPage';
import { PagarFacturaPage } from '@/features/administracion/PagarFacturaPage';
import { PagosPage } from '@/features/pagos/PagosPage';
import { InventarioPage } from '@/features/inventario/InventarioPage';
import { RecepcionPage } from '@/features/recepcion/RecepcionPage';
import { MantenimientoPage } from '@/features/mantenimiento/MantenimientoPage';
import { DetalleMantenimientoPage } from '@/features/mantenimiento/DetalleMantenimientoPage';
import { TesoreriaPage } from '@/features/tesoreria/TesoreriaPage';
import { TesoreriaCajaPage } from '@/features/tesoreria/TesoreriaCajaPage';
import { AprobacionesPage } from '@/features/aprobaciones/AprobacionesPage';
import { ReportesPage } from '@/features/reportes/ReportesPage';
import { AdministracionPage } from '@/features/administracion/AdministracionPage';
import { ConfiguracionPage } from '@/features/configuracion/ConfiguracionPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { ContactosPage } from '@/features/contactos/ContactosPage';
import { ChatBotPage } from '@/features/chatbot/ChatBotPage';

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    // Each route lists exactly the roles allowed in the AppShell sidebar
    // — keep this in sync with `NAV_ITEMS` in AppShell.tsx. Without an
    // explicit allowlist a route is reachable by URL even when the nav
    // hides it (e.g. mantenimiento navigating to `/contactos` directly).
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute menuKey="home" allowedRoles={['admin', 'administracion', 'ventas', 'marketing']}>
            <HomePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'contactos',
        element: (
          <ProtectedRoute menuKey="contactos" allowedRoles={['admin', 'administracion', 'ventas', 'marketing']}>
            <ContactosPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'ventas',
        element: (
          // Nova can browse the list but only sees Nova-flow rows (filtered
          // in VentasPage) and the eye action jumps straight to /pagos —
          // they're not allowed in /ventas/nueva or the editable detail
          // page below, so those allowlists deliberately omit `nova`.
          <ProtectedRoute menuKey="ventas" allowedRoles={['admin', 'administracion', 'ventas', 'nova', 'marketing']}>
            <VentasPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'ventas/nueva',
        element: (
          // Marketing can create its own ventas, so it's allowlisted here.
          // No `denyReadOnly`: marketing is read-only globally (that flag would
          // bounce it), and the only OTHER read-only role — visitante — is
          // already excluded by the allowlist.
          <ProtectedRoute allowedRoles={['admin', 'administracion', 'ventas', 'marketing']}>
            <NuevaCotizacionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'ventas/:id',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'administracion', 'ventas', 'marketing']}>
            <DetalleVentaPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'ventas/:id/pagos',
        element: (
          // Nova's only allowed venta surface — the Pagos screen, locked
          // to read-only in the page itself (alongside the existing
          // fromAdmin / isAdministracion / isVisitante locks).
          <ProtectedRoute allowedRoles={['admin', 'administracion', 'ventas', 'nova', 'marketing']}>
            <DetalleVentaPagosPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'compras',
        element: (
          <ProtectedRoute menuKey="compras" allowedRoles={['admin', 'administracion']}>
            <ComprasPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'compras/nueva',
        element: (
          <ProtectedRoute
            allowedRoles={['admin', 'administracion']}
            denyReadOnly
            readOnlyFallback="/compras"
          >
            <NuevaCompraPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'compras/:id',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'administracion']}>
            <VerCompraPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'compras/:id/editar',
        element: (
          <ProtectedRoute
            allowedRoles={['admin', 'administracion']}
            denyReadOnly
            readOnlyFallback="/compras"
          >
            <NuevaCompraPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'pagos',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'administracion', 'ventas', 'marketing']}>
            <PagosPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventario',
        element: (
          <ProtectedRoute menuKey="inventario" allowedRoles={['admin', 'administracion', 'ventas', 'mantenimiento', 'marketing']}>
            <InventarioPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'recepcion',
        element: (
          <ProtectedRoute menuKey="recepcion" allowedRoles={['admin', 'administracion', 'mantenimiento']}>
            <RecepcionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'mantenimiento',
        element: (
          <ProtectedRoute menuKey="mantenimiento" allowedRoles={['admin', 'administracion', 'mantenimiento']}>
            <MantenimientoPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'mantenimiento/:id',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'administracion', 'mantenimiento']}>
            <DetalleMantenimientoPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'administracion',
        element: (
          <ProtectedRoute menuKey="administracion" allowedRoles={['admin', 'administracion', 'nova']}>
            <AdministracionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'administracion/pagar/:compraId',
        element: (
          <ProtectedRoute
            allowedRoles={['admin', 'administracion']}
            denyReadOnly
            readOnlyFallback="/administracion"
          >
            <PagarFacturaPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'configuracion',
        element: (
          <ProtectedRoute menuKey="configuracion" allowedRoles={['admin', 'administracion']}>
            <ConfiguracionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'aprobaciones',
        element: (
          <ProtectedRoute menuKey="aprobaciones" allowedRoles={['admin']} denyReadOnly>
            <AprobacionesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reporting',
        // `nova` reaches Reportes but only the Nova subset — the page
        // filters the dropdown and `reportes.ts` enforces it.
        element: (
          <ProtectedRoute menuKey="reportes" allowedRoles={['admin', 'administracion', 'nova']}>
            <ReportesPage />
          </ProtectedRoute>
        ),
      },
      // Facturación ya no es un módulo propio: vive dentro de Reportes
      // (`reportId === 'facturacion'`, AR-only) desde 2026-07-21.
      {
        path: 'tesoreria',
        element: (
          <ProtectedRoute menuKey="tesoreria" allowedRoles={['admin', 'administracion', 'nova']}>
            <TesoreriaPage />
          </ProtectedRoute>
        ),
      },
      {
        // Detalle de una caja. El nombre va encodeado en la URL ('Nova
        // Mercado Pago'); mismo gate de rol que el resumen.
        path: 'tesoreria/:caja',
        element: (
          <ProtectedRoute menuKey="tesoreria" allowedRoles={['admin', 'administracion', 'nova']}>
            <TesoreriaCajaPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute menuKey="dashboard" allowedRoles={['admin']}>
            <DashboardPage />
          </ProtectedRoute>
        ),
      },
      { path: 'chatbot', element: <ChatBotPage /> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
